/**
 * The boom HUD (M10 P5a12c; review follow-up "a HUD readout and beam draw for booms 1–3"): a four-pip heat strip,
 * one pip per boom, and a placeholder ring at each marked rock, from `MiningReadout.booms` (written only on a boom
 * hull). The model is pure of the DOM; the view mounts its elements on the first boom-hull frame, so an S/M HUD
 * never gets them and stays byte-identical. Every marker goes through `projectWorld` (camera-rebased, D-013).
 * `updateBoomHud` and `renderBoomHud` allocate nothing per frame; the view writes a style only when its rounded
 * value changes (the M2 view's rule).
 */
import { MAX_MINING_BOOMS } from '../data/schemas'
import type { BoomReadout } from '../space/contracts'
import { createScreenPoint, projectWorld, type HudCamera, type ScreenPoint } from './projection'

/** Pip states: no boom in that slot, idle (no mark or the hold is full), firing, overheated. */
export const PIP = { none: 0, idle: 1, on: 2, overheat: 3 } as const
export type PipState = (typeof PIP)[keyof typeof PIP]

/** `data-state` per pip state (constant strings: the view never builds one per frame). */
export const PIP_STATE_ATTR: readonly string[] = ['none', 'idle', 'on', 'overheat']

/** Placeholder mark ring tuning (`m2hud.json` `boomRing`): the ring is the rock's projected radius times `scale`, clamped to [min, max] px. */
export interface BoomHudTuning {
  ringScale: number
  ringMinPx: number
  ringMaxPx: number
}

export interface BoomHud {
  /** Booms on the hull (0 or 1 = no boom HUD). */
  count: number
  /** The hold is full: every pip idles. */
  holdFull: boolean
  readonly pip: Uint8Array
  readonly heat: Float64Array
  /** Mark rings: on screen, centre and radius (CSS px). */
  marks: number
  readonly ringOn: Uint8Array
  readonly ringX: Float64Array
  readonly ringY: Float64Array
  readonly ringR: Float64Array
}

export function createBoomHud(): BoomHud {
  return {
    count: 0,
    holdFull: false,
    pip: new Uint8Array(MAX_MINING_BOOMS),
    heat: new Float64Array(MAX_MINING_BOOMS),
    marks: 0,
    ringOn: new Uint8Array(MAX_MINING_BOOMS),
    ringX: new Float64Array(MAX_MINING_BOOMS),
    ringY: new Float64Array(MAX_MINING_BOOMS),
    ringR: new Float64Array(MAX_MINING_BOOMS),
  }
}

const ringPoint: ScreenPoint = createScreenPoint()

/** Fills the pips and rings from the readout (`undefined` or one boom = no boom HUD). Allocation-free. */
export function updateBoomHud(h: BoomHud, booms: Readonly<BoomReadout> | undefined, cam: HudCamera, tuning: Readonly<BoomHudTuning>): void {
  const n = booms === undefined || booms.count <= 1 ? 0 : booms.count
  h.count = n
  h.holdFull = n > 0 && booms !== undefined && booms.holdFull
  h.marks = 0
  for (let k = 0; k < MAX_MINING_BOOMS; k++) {
    h.ringOn[k] = 0
    if (booms === undefined || k >= n) {
      h.pip[k] = PIP.none
      h.heat[k] = 0
      continue
    }
    const heat = booms.heat[k]
    h.heat[k] = heat < 0 ? 0 : heat > 1 ? 1 : heat
    h.pip[k] = booms.overheated[k] === 1 ? PIP.overheat : booms.on[k] === 1 ? PIP.on : PIP.idle
  }
  if (booms === undefined || n === 0) return
  h.marks = booms.marks
  for (let i = 0; i < booms.marks && i < MAX_MINING_BOOMS; i++) {
    if (!projectWorld(cam, booms.markPos[i * 3], booms.markPos[i * 3 + 1], booms.markPos[i * 3 + 2], 0, ringPoint)) continue
    const r = booms.markR[i] * ringPoint.pxPerMeter * tuning.ringScale
    h.ringOn[i] = 1
    h.ringX[i] = ringPoint.x
    h.ringY[i] = ringPoint.y
    h.ringR[i] = r < tuning.ringMinPx ? tuning.ringMinPx : r > tuning.ringMaxPx ? tuning.ringMaxPx : r
  }
}

/** The DOM surface the view drives (an element, or a recorder in tests). */
export interface BoomNode {
  readonly style: { transform: string; display: string; width: string; height: string }
  setAttribute(name: string, value: string): void
}

export interface BoomHudElements {
  root: BoomNode
  pips: readonly BoomNode[]
  fills: readonly BoomNode[]
  rings: readonly BoomNode[]
}

/** Builds the elements under `host` with `make` (once, on the first boom-hull frame). */
export function buildBoomHudElements<N extends BoomNode>(host: N, make: (cls: string, parent: N) => N): BoomHudElements {
  const root = make('boom-hud', host)
  const pips: BoomNode[] = []
  const fills: BoomNode[] = []
  const rings: BoomNode[] = []
  const strip = make('boom-pips', root)
  for (let k = 0; k < MAX_MINING_BOOMS; k++) {
    const pip = make('boom-pip', strip)
    pips.push(pip)
    fills.push(make('boom-pip-fill', pip))
  }
  for (let k = 0; k < MAX_MINING_BOOMS; k++) rings.push(make('boom-ring', root))
  return { root, pips, fills, rings }
}

export interface BoomHudView {
  /** Null until the first boom-hull frame mounts the elements. */
  el: BoomHudElements | null
  readonly mount: () => BoomHudElements
  shown: number
  readonly pipState: Int8Array
  readonly fill: Float64Array
  readonly ringShown: Int8Array
  readonly ringX: Float64Array
  readonly ringY: Float64Array
  readonly ringR: Float64Array
}

export function createBoomHudView(mount: () => BoomHudElements): BoomHudView {
  return {
    el: null,
    mount,
    shown: -1,
    pipState: new Int8Array(MAX_MINING_BOOMS).fill(-1),
    fill: new Float64Array(MAX_MINING_BOOMS).fill(-1),
    ringShown: new Int8Array(MAX_MINING_BOOMS).fill(-1),
    ringX: new Float64Array(MAX_MINING_BOOMS).fill(NaN),
    ringY: new Float64Array(MAX_MINING_BOOMS).fill(NaN),
    ringR: new Float64Array(MAX_MINING_BOOMS).fill(NaN),
  }
}

/** Writes the diffed boom HUD. No boom hull and never mounted: touches nothing. Allocation-free while steady. */
export function renderBoomHud(v: BoomHudView, h: Readonly<BoomHud>): void {
  if (h.count === 0 && v.el === null) return
  if (v.el === null) v.el = v.mount()
  const el = v.el
  const shown = h.count > 0 ? 1 : 0
  if (shown !== v.shown) {
    v.shown = shown
    el.root.style.display = shown === 1 ? '' : 'none'
  }
  if (shown === 0) return
  for (let k = 0; k < MAX_MINING_BOOMS; k++) {
    const st = h.holdFull && h.pip[k] !== PIP.none && h.pip[k] !== PIP.overheat ? PIP.idle : h.pip[k]
    if (st !== v.pipState[k]) {
      v.pipState[k] = st
      el.pips[k].setAttribute('data-state', PIP_STATE_ATTR[st])
    }
    const q = Math.round(h.heat[k] * 100) / 100
    if (q !== v.fill[k]) {
      v.fill[k] = q
      el.fills[k].style.transform = `scaleY(${q})`
    }
    const on = h.ringOn[k]
    if (on !== v.ringShown[k]) {
      v.ringShown[k] = on
      el.rings[k].style.display = on === 1 ? '' : 'none'
    }
    if (on === 0) continue
    const x = Math.round(h.ringX[k])
    const y = Math.round(h.ringY[k])
    const r = Math.round(h.ringR[k])
    if (x !== v.ringX[k] || y !== v.ringY[k]) {
      v.ringX[k] = x
      v.ringY[k] = y
      el.rings[k].style.transform = `translate3d(${x - r}px,${y - r}px,0)`
    }
    if (r !== v.ringR[k]) {
      v.ringR[k] = r
      el.rings[k].style.width = `${2 * r}px`
      el.rings[k].style.height = `${2 * r}px`
      el.rings[k].style.transform = `translate3d(${x - r}px,${y - r}px,0)`
    }
  }
}
